/**
 * Persists flattened J-code strings on {@link UserStore} for downstream screens.
 *
 * @module setJCodesInLocalStorage
 */
import { AuthOnFile, MedicalPaOrder } from "../data-model/medicalPaOrdersModel";
import {
  getAllJCodes,
  getAllJCodesFromAuthOnFile,
} from "../pages/singleOrderLayout/utils/existingDocsToLs";
import {
  UserGeneralInfoContextKeys,
  UserStore,
} from "../store/UserGeneralInfoStore";

/**
 * Stores all extracted J-codes from a Medical PA Order into the singleton store.
 *
 * This function retrieves all J-codes associated with the given medical PA order,
 * normalizes them into an array of strings, and saves them under a unique key in
 * the singleton store. If no J-codes are found, an empty array is stored instead.
 *
 * @function setJCodesInLocalStorage
 * @param {MedicalPaOrder} singleMedicalPaOrder - The medical PA order containing J-code information.
 * @returns {void} This function does not return a value.
 *
 * @example
 * const order = { id: "123", ... };
 * setJCodesInLocalStorage(order);
 * // UserStore["J_CODES_123"] = ["J1234", "J5678"];
 *
 * @remarks
 * - Gracefully handles errors and logs them to the console.
 * - Filters out null/undefined J-codes and replaces them with empty strings.
 * - Ensures store always receives a valid array (never null or undefined).
 */

export const setJCodesInLocalStorage = (
  singleMedicalPaOrder: MedicalPaOrder,
) => {
  try {
    const jCodes = getAllJCodes(singleMedicalPaOrder);
    if (jCodes && jCodes.length > 0) {
      const jCodesArray = jCodes.map((jCode) => jCode?.j_code ?? "");
      UserStore.setItem(
        `${UserGeneralInfoContextKeys.J_CODES}_${singleMedicalPaOrder?.id}`,
        jCodesArray ?? [],
      );
    } else {
      UserStore.setItem(
        `${UserGeneralInfoContextKeys.J_CODES}_${singleMedicalPaOrder?.id}`,
        [],
      );
    }
  } catch (error) {
    console.error("Error setting J codes in local storage:", error);
  }
};

/**
 * Stores all extracted J-codes from an AuthOnFile object into the singleton store.
 *
 * Same behavior as {@link setJCodesInLocalStorage} but accepts AuthOnFile
 * and an orderId directly instead of the full MedicalPaOrder.
 *
 * @param authOnFile - The auth-on-file object containing entries with J-codes
 * @param orderId - The order ID used as part of the storage key
 */
export const setJCodesFromAuthOnFile = (
  authOnFile: AuthOnFile | undefined,
  orderId: string,
) => {
  const storeKey = `${UserGeneralInfoContextKeys.J_CODES}_${orderId}`;
  try {
    const jCodes = getAllJCodesFromAuthOnFile(authOnFile);
    if (jCodes && jCodes.length > 0) {
      UserStore.setItem(
        storeKey,
        jCodes.map((jCode) => jCode?.j_code ?? ""),
      );
    } else {
      UserStore.setItem(storeKey, []);
    }
  } catch (error) {
    console.error("Error setting J codes in local storage:", error);
  }
};
